import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  House, Calendar, Users, CurrencyInr, Plus, Trash, X, Buildings, Clock, MapPin, ChartBar
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function DoctorPractice() {
  const [tab, setTab] = useState('clinics');

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="doctor-practice-page">
      <Navbar />
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <Link to="/doctor" className="text-sm text-secondary hover:text-primary inline-flex items-center gap-1">← Back to Dashboard</Link>
            <h1 className="font-spectral text-3xl font-bold text-primary mt-2">My Practice</h1>
            <p className="text-secondary text-sm">Manage clinics, appointment slots, walk-in patients and view revenue.</p>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-surface mb-6 flex-wrap h-auto">
              <TabsTrigger value="clinics" data-testid="tab-clinics"><Buildings size={16} weight="bold" className="mr-1" /> Clinics</TabsTrigger>
              <TabsTrigger value="slots" data-testid="tab-slots"><Clock size={16} weight="bold" className="mr-1" /> Slots</TabsTrigger>
              <TabsTrigger value="appointments" data-testid="tab-appointments"><Calendar size={16} weight="bold" className="mr-1" /> Appointments</TabsTrigger>
              <TabsTrigger value="opd" data-testid="tab-opd"><Users size={16} weight="bold" className="mr-1" /> OPD Log</TabsTrigger>
              <TabsTrigger value="revenue" data-testid="tab-revenue"><ChartBar size={16} weight="bold" className="mr-1" /> Revenue</TabsTrigger>
            </TabsList>
            <TabsContent value="clinics"><ClinicsTab /></TabsContent>
            <TabsContent value="slots"><SlotsTab /></TabsContent>
            <TabsContent value="appointments"><AppointmentsTab /></TabsContent>
            <TabsContent value="opd"><OpdTab /></TabsContent>
            <TabsContent value="revenue"><RevenueTab /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

// ============ CLINICS TAB ============
function ClinicsTab() {
  const [clinics, setClinics] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', phone: '', google_maps_url: '', timings: '', consultation_fee: 500, consultation_days: [] });

  const fetchClinics = async () => {
    try {
      const { data } = await axios.get(`${API}/practice/clinics`, { withCredentials: true });
      setClinics(data.clinics || []);
    } catch (e) { toast.error('Failed to load clinics'); }
  };
  useEffect(() => { fetchClinics(); }, []);

  const toggleDay = (d) => {
    setForm(prev => ({ ...prev, consultation_days: prev.consultation_days.includes(d) ? prev.consultation_days.filter(x => x !== d) : [...prev.consultation_days, d] }));
  };

  const handleSave = async () => {
    if (!form.name || !form.address) { toast.error('Name and address required'); return; }
    try {
      await axios.post(`${API}/practice/clinics`, form, { withCredentials: true });
      toast.success('Clinic added');
      setShowForm(false);
      setForm({ name: '', address: '', phone: '', google_maps_url: '', timings: '', consultation_fee: 500, consultation_days: [] });
      fetchClinics();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this clinic?')) return;
    await axios.delete(`${API}/practice/clinics/${id}`, { withCredentials: true });
    fetchClinics();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-secondary">{clinics.length} clinic{clinics.length !== 1 ? 's' : ''} added</p>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm inline-flex items-center gap-1 px-4 py-2" data-testid="add-clinic-button">
          <Plus size={14} weight="bold" /> {showForm ? 'Cancel' : 'Add Clinic'}
        </button>
      </div>

      {showForm && (
        <div className="card space-y-3" data-testid="clinic-form">
          <input type="text" placeholder="Clinic Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-primary/20 rounded-lg" data-testid="clinic-name-input" />
          <textarea placeholder="Full Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={2} className="w-full px-3 py-2 border border-primary/20 rounded-lg" data-testid="clinic-address-input" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="tel" placeholder="Clinic Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
            <input type="number" placeholder="Consultation Fee (₹)" value={form.consultation_fee} onChange={e => setForm({ ...form, consultation_fee: parseInt(e.target.value) || 500 })} className="px-3 py-2 border border-primary/20 rounded-lg" />
          </div>
          <input type="url" placeholder="Google Maps URL (optional)" value={form.google_maps_url} onChange={e => setForm({ ...form, google_maps_url: e.target.value })} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <input type="text" placeholder="Timings, e.g., Mon-Fri 10AM-1PM, 5PM-8PM" value={form.timings} onChange={e => setForm({ ...form, timings: e.target.value })} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <div className="flex flex-wrap gap-2">
            {DAYS.map(d => (
              <button key={d} type="button" onClick={() => toggleDay(d)} className={`px-3 py-1 text-xs rounded-full border ${form.consultation_days.includes(d) ? 'bg-primary text-white border-primary' : 'border-primary/30 text-primary'}`}>{d}</button>
            ))}
          </div>
          <button onClick={handleSave} className="btn-primary w-full py-2" data-testid="save-clinic-button">Save Clinic</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {clinics.map((c, idx) => (
          <div key={c.id} className="card" data-testid={`clinic-card-${idx}`}>
            <div className="flex justify-between items-start gap-2 mb-2">
              <div>
                <h3 className="font-semibold text-primary">{c.name}</h3>
                <p className="text-xs text-secondary inline-flex items-center gap-1 mt-1"><MapPin size={12} /> {c.address}</p>
              </div>
              <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`delete-clinic-${idx}`}><Trash size={14} /></button>
            </div>
            {c.phone && <p className="text-xs text-secondary">📞 {c.phone}</p>}
            {c.timings && <p className="text-xs text-secondary mt-1">🕐 {c.timings}</p>}
            <p className="text-sm font-medium text-primary mt-2">Fee: ₹{c.consultation_fee}</p>
            {(c.consultation_days || []).length > 0 && <p className="text-xs text-secondary mt-1">Open: {c.consultation_days.join(', ')}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ SLOTS TAB ============
function SlotsTab() {
  const [clinics, setClinics] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ clinic_id: '', date: '', start_time: '10:00', end_time: '10:30' });

  const fetchAll = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/practice/clinics`, { withCredentials: true }),
        axios.get(`${API}/practice/slots?from_date=${new Date().toISOString().split('T')[0]}`, { withCredentials: true }),
      ]);
      setClinics(c.data.clinics || []);
      setSlots(s.data.slots || []);
      if (!form.clinic_id && c.data.clinics?.length) setForm(f => ({ ...f, clinic_id: c.data.clinics[0].id }));
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    if (!form.clinic_id || !form.date) { toast.error('Pick clinic and date'); return; }
    try {
      await axios.post(`${API}/practice/slots`, form, { withCredentials: true });
      toast.success('Slot added');
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleBlock = async (id) => {
    await axios.post(`${API}/practice/slots/${id}/block`, {}, { withCredentials: true });
    fetchAll();
  };
  const del = async (id) => {
    await axios.delete(`${API}/practice/slots/${id}`, { withCredentials: true });
    fetchAll();
  };

  return (
    <div className="space-y-4">
      <div className="card" data-testid="slot-form">
        <h3 className="font-semibold text-primary mb-3">Add a Slot</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <select value={form.clinic_id} onChange={e => setForm({ ...form, clinic_id: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" data-testid="slot-clinic-select">
            <option value="">Pick clinic...</option>
            {clinics.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" data-testid="slot-date-input" />
          <input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
          <input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
        </div>
        <button onClick={handleCreate} className="btn-primary mt-3 px-4 py-2 text-sm" data-testid="create-slot-button">+ Add Slot</button>
      </div>

      <div className="card">
        <h3 className="font-semibold text-primary mb-3">Upcoming Slots ({slots.length})</h3>
        {slots.length === 0 ? <p className="text-sm text-secondary">No slots yet.</p> : (
          <div className="space-y-2">
            {slots.map((s, idx) => {
              const clinic = clinics.find(c => c.id === s.clinic_id);
              return (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-primary/10 bg-surface" data-testid={`slot-row-${idx}`}>
                  <div className="text-sm">
                    <span className="font-medium text-primary">{s.date}</span>
                    <span className="text-secondary mx-2">·</span>
                    <span>{s.start_time} - {s.end_time}</span>
                    {clinic && <span className="text-xs text-secondary block">{clinic.name}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.booked ? <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">Booked</span> :
                      s.blocked ? <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">Blocked</span> :
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">Open</span>}
                    {!s.booked && (
                      <>
                        <button onClick={() => toggleBlock(s.id)} className="text-xs text-primary underline" data-testid={`block-slot-${idx}`}>{s.blocked ? 'Unblock' : 'Block'}</button>
                        <button onClick={() => del(s.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash size={12} /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ APPOINTMENTS TAB ============
function AppointmentsTab() {
  const [appts, setAppts] = useState([]);
  const fetchAppts = async () => {
    try {
      const { data } = await axios.get(`${API}/practice/appointments`, { withCredentials: true });
      setAppts(data.appointments || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchAppts(); }, []);

  const updateStatus = async (id, status) => {
    await axios.post(`${API}/practice/appointments/${id}/status?status=${status}`, {}, { withCredentials: true });
    toast.success(`Marked ${status}`);
    fetchAppts();
  };

  return (
    <div className="card">
      <h3 className="font-semibold text-primary mb-4">Appointments ({appts.length})</h3>
      {appts.length === 0 ? <p className="text-sm text-secondary">No appointments yet. Share your booking link with patients to start receiving bookings.</p> : (
        <div className="space-y-3">
          {appts.map((a, idx) => (
            <div key={a.id} className="p-3 border border-primary/10 rounded-lg bg-surface" data-testid={`appt-row-${idx}`}>
              <div className="flex justify-between items-start flex-wrap gap-2">
                <div>
                  <p className="font-medium text-primary">{a.patient_name} <span className="text-xs text-secondary">({a.patient_phone})</span></p>
                  <p className="text-sm text-secondary mt-1">{a.date} · {a.start_time}-{a.end_time}{a.clinic_name && ` · ${a.clinic_name}`}</p>
                  {a.reason && <p className="text-xs text-secondary mt-1">Reason: {a.reason}</p>}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  a.status === 'completed' ? 'bg-green-100 text-green-800' :
                  a.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  a.status === 'no_show' ? 'bg-amber-100 text-amber-800' :
                  'bg-primary/10 text-primary'
                }`}>{a.status}</span>
              </div>
              {a.status === 'confirmed' && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => updateStatus(a.id, 'completed')} className="text-xs px-3 py-1 bg-primary text-white rounded-full" data-testid={`complete-appt-${idx}`}>Mark Completed</button>
                  <button onClick={() => updateStatus(a.id, 'no_show')} className="text-xs px-3 py-1 border border-amber-300 text-amber-700 rounded-full">No-show</button>
                  <button onClick={() => updateStatus(a.id, 'cancelled')} className="text-xs px-3 py-1 border border-red-300 text-red-600 rounded-full">Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ OPD TAB ============
function OpdTab() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', age: '', gender: 'M', mobile: '', address: '', diagnosis: '', notes: '', follow_up_date: '' });

  const fetchPatients = async (q = '') => {
    try {
      const { data } = await axios.get(`${API}/practice/opd-patients${q ? '?q=' + encodeURIComponent(q) : ''}`, { withCredentials: true });
      setPatients(data.patients || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchPatients(); }, []);

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      await axios.post(`${API}/practice/opd-patients`, payload, { withCredentials: true });
      toast.success('Patient added');
      setShowForm(false);
      setForm({ name: '', age: '', gender: 'M', mobile: '', address: '', diagnosis: '', notes: '', follow_up_date: '' });
      fetchPatients();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center justify-between">
        <input type="text" placeholder="Search by name or mobile..." value={search} onChange={e => { setSearch(e.target.value); fetchPatients(e.target.value); }} className="flex-1 px-3 py-2 border border-primary/20 rounded-lg" data-testid="opd-search-input" />
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm inline-flex items-center gap-1 px-4 py-2" data-testid="add-opd-button">
          <Plus size={14} weight="bold" /> {showForm ? 'Cancel' : 'Add Patient'}
        </button>
      </div>

      {showForm && (
        <div className="card space-y-3" data-testid="opd-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" placeholder="Patient Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" data-testid="opd-name-input" />
            <input type="tel" placeholder="Mobile" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
            <input type="number" placeholder="Age" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
            <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg">
              <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
            </select>
          </div>
          <input type="text" placeholder="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <textarea placeholder="Diagnosis" value={form.diagnosis} onChange={e => setForm({ ...form, diagnosis: e.target.value })} rows={2} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <textarea placeholder="Notes (treatment, advice, etc.)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <label className="block text-xs text-secondary">Follow-up Date</label>
          <input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
          <button onClick={handleSave} className="btn-primary w-full py-2" data-testid="save-opd-button">Save Patient</button>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold text-primary mb-3">Walk-in Patients ({patients.length})</h3>
        {patients.length === 0 ? <p className="text-sm text-secondary">No OPD patients yet.</p> : (
          <div className="space-y-2">
            {patients.map((p, idx) => (
              <div key={p.id} className="p-3 border border-primary/10 rounded-lg bg-surface" data-testid={`opd-row-${idx}`}>
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-primary">{p.name} <span className="text-xs text-secondary">{p.age ? `· ${p.age}y` : ''} {p.gender ? `· ${p.gender}` : ''}</span></p>
                    {p.mobile && <p className="text-xs text-secondary">{p.mobile}</p>}
                    {p.diagnosis && <p className="text-sm text-primary mt-1"><strong>Dx:</strong> {p.diagnosis}</p>}
                    {p.notes && <p className="text-xs text-secondary mt-1">{p.notes}</p>}
                  </div>
                  <div className="text-right text-xs text-secondary">
                    {p.follow_up_date && <p className="text-accent font-medium">Follow-up: {p.follow_up_date}</p>}
                    <p>{p.created_at?.split('T')[0]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ REVENUE TAB ============
function RevenueTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/practice/revenue`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-secondary">Loading...</p>;
  const max = Math.max(1, ...data.daily_series.map(d => d.revenue));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card text-center">
          <p className="text-xs text-secondary uppercase tracking-wide mb-1">Today</p>
          <p className="font-spectral text-3xl font-bold text-primary">₹{data.today_revenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="card text-center">
          <p className="text-xs text-secondary uppercase tracking-wide mb-1">Lifetime Revenue</p>
          <p className="font-spectral text-3xl font-bold text-primary">₹{data.total_revenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="card text-center">
          <p className="text-xs text-secondary uppercase tracking-wide mb-1">Appointments Completed</p>
          <p className="font-spectral text-3xl font-bold text-primary">{data.total_appointments}</p>
        </div>
      </div>
      <div className="card">
        <h3 className="font-semibold text-primary mb-4">Last 30 Days</h3>
        <div className="flex items-end gap-1 h-40" data-testid="revenue-chart">
          {data.daily_series.map((d, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ₹${d.revenue}`}>
              <div className="w-full rounded-t bg-primary" style={{ height: `${(d.revenue / max) * 100}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-secondary mt-2">
          <span>{data.daily_series[0]?.date}</span>
          <span>{data.daily_series[data.daily_series.length - 1]?.date}</span>
        </div>
      </div>
    </div>
  );
}
